/**
 * Семантическое ядро — то, за что клиент платит.
 *
 * Единица тарификации — отслеживаемый вопрос (спека §10), поэтому наверху стоит
 * не заголовок, а счётчик квоты: сколько вопросов занято из того, что даёт
 * тариф.
 *
 * Три правила, которые определили устройство экрана.
 *
 * 1. **Имени бренда в постоянном ядре быть не должно.** Вопрос с названием
 *    внутри помечается прямо в строке: упоминание в ответе на такой вопрос
 *    ничего не доказывает, и из расчёта видимости он выпадает.
 * 2. **Правка текста заводит новую версию, старая выключается.** Ядро на период
 *    сравнения обязано быть неизменным, иначе связка «рекомендация → цифра
 *    выросла» ничего не доказывает. Экран говорит это до нажатия, а не после.
 * 3. **Ядро меняется явным действием.** Генерация и пакеты возвращают
 *    кандидатов; в ядро они попадают одной пачкой, и ревизия заводится одна.
 * 4. **Пересборка спрашивается до нажатия.** Она заменяет набор целиком и
 *    заводит границу версий в истории графиков: сравнивать «до» и «после» через
 *    неё нельзя. Сказать об этом после — значит не сказать вовсе.
 */

import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, type Page } from '../../api/client';
import { ApiError, fieldErrors } from '../../api/problem';
import { useAccess } from '../../session';
import { useScope } from '../../scope';
import { useLoad } from '../../ui/load';
import { useScrollToTop } from '../../ui/scroll';
import { ScanCadencePicker } from '../../ui/cadence';
import { Confirm, SelectField, Switch, TextArea } from '../../ui/fields';
import { Empty, ErrorNotice, Skeleton } from '../../ui/state';
import {
  isPreparing,
  PreparationFailed,
  Preparing,
  rebuildQuestions,
  usePreparation,
  useRebuildAttempt,
} from '../../ui/preparation';
import { FUNNEL_ORDER, funnelStage, questionSource } from '../../ui/labels';
import { Generate } from './Generate';
import { Import } from './Import';
import { Packs } from './Packs';
import { countOf, coverageOf, int } from '@shared/src/format';
import type { components } from '../../api/schema';
import { pickBrand, scanBlockedFallback } from '../../ui/screen-states';
import { CORE_SIZE_RULE } from '@shared/src/product';

type Question = components['schemas']['Question'];
type FunnelStage = components['schemas']['FunnelStage'];
type Quotas = components['schemas']['OrganizationQuotas'];
type Brand = components['schemas']['Brand'];
type BulkResult = components['schemas']['QuestionBulkResult'];

type Mode = 'list' | 'generate' | 'packs' | 'add' | 'import';

interface Filters {
  active: boolean;
  stage: FunnelStage | '';
  cluster: string;
}

const NO_FILTERS: Filters = { active: true, stage: '', cluster: '' };

export function Questions() {
  const scope = useScope();
  const access = useAccess();
  const brandId = scope.brandId;

  const [mode, setMode] = useState<Mode>('list');
  const [filters, setFilters] = useState<Filters>(NO_FILTERS);
  const [editing, setEditing] = useState<Question | null>(null);
  const [draft, setDraft] = useState('');
  const [newText, setNewText] = useState('');
  const [newStage, setNewStage] = useState<FunnelStage>('decision');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Question | null>(null);
  const [rebuildAsked, setRebuildAsked] = useState(false);
  const attempt = useRebuildAttempt();
  /* Бренд, каким его знает этот экран. Ответ пересборки важнее списка области:
     он приходит с состоянием `preparing` сразу, а `scope` перечитывается
     позже — и без этого кнопка на мгновение возвращалась бы на место. */
  const [prepared, setPrepared] = useState<Brand | null>(null);

  // Список, генерация, кандидаты и пакеты живут по одному адресу и очень
  // разной высоты. Без возврата к началу человек, нажавший «Придумать вопросы»
  // внизу длинного ядра, получает таблицу кандидатов, заголовок которой уже
  // уехал за верхний край.
  useScrollToTop(mode);

  const key = brandId
    ? `questions:${brandId}:${filters.active}:${filters.stage}:${filters.cluster}`
    : 'questions:none';

  const list = useLoad<Page<Question> | null>(key, (signal) =>
    brandId
      ? api.get<Page<Question>>(`/brands/${brandId}/questions`, {
          signal,
          query: {
            limit: 200,
            active: filters.active,
            ...(filters.stage ? { funnel_stage: filters.stage } : {}),
            ...(filters.cluster ? { cluster_key: filters.cluster } : {}),
          },
        })
      : Promise.resolve(null),
  );

  const quotas = useLoad<Quotas | null>('quotas', (signal) =>
    access.reaches('GET', '/organizations/current/quotas')
      ? api.get<Quotas>('/organizations/current/quotas', { signal })
      : Promise.resolve(null),
  );

  const brand = prepared?.id === brandId ? prepared : scope.brand;
  const preparing = isPreparing(brand);
  // Опрос идёт, только пока подготовка идёт: закончилась — перечитываем ядро и
  // квоту, потому что набор заменился целиком, а вместе с ним и занятые места.
  const watch = usePreparation(preparing && brandId ? brandId : null, (fresh) => {
    setPrepared(fresh);
    list.reload();
    quotas.reload();
    scope.reload();
    if (fresh.preparation_state === 'ready') {
      setBanner(
        'Ядро готово — это его новая версия. Точки до неё и после сравнивать нельзя: вопросы сменились целиком.',
      );
    }
  });

  const quota = quotas.state.kind === 'ready' ? quotas.state.data : null;
  const free = quota
    ? Math.max(0, quota.tracked_questions - (quota.questions_used ?? 0))
    : null;
  // ПОКА ИДЁТ ПЕРЕСБОРКА, ЯДРО ТОЛЬКО ЧИТАЕТСЯ. Скрыть кнопки шапки мало:
  // правка текста и выключение вопроса в таблице
  // записались бы успешно и через минуту исчезли вместе со всем набором — то
  // есть продукт молча съел бы работу, которую сам же позволил сделать.
  const readOnly =
    !access.reaches('POST', '/brands/{brandId}/questions') ||
    preparing ||
    busy === 'rebuild';
  const canPay = access.reaches('POST', '/billing/subscription/plan');
  /* Места в ядре нет. `null` — роль не читает квоты: тогда не гадаем и не гасим
     ничего, а слушаем сервер. */
  const noRoom = free !== null && free <= 0;
  /* Тарифа нет вовсе: подготовка такому бренду не положена. */
  const noPlan = quota !== null && !quota.plan_code;

  const items = useMemo(
    () => (list.state.kind === 'ready' && list.state.data ? list.state.data.items : []),
    [list.state],
  );

  // Темы берутся из того, что реально пришло: отдельного справочника кластеров
  // в контракте нет, и придумывать его на клиенте значило бы завести вторую
  // копию правды.
  const clusters = useMemo(() => {
    const seen = new Map<string, string>();
    for (const question of items) {
      if (question.cluster_key) {
        seen.set(question.cluster_key, question.cluster_label ?? question.cluster_key);
      }
    }
    return [...seen.entries()];
  }, [items]);

  // Отбор задан человеком: под ним список — это не всё ядро, и то, что по
  // нему видно, нельзя выдавать за его состав.
  const filtered = filters.stage !== '' || filters.cluster !== '' || !filters.active;

  /* Сколько вопросов в ядре бренда — но только когда экран знает это наверняка:
     без фильтров и без второй страницы. Иначе замена обещала бы выключить двести
     вопросов бренду, у которого их четыреста, и считала бы по ним же места. */
  const own =
    !filtered && list.state.kind === 'ready' && !list.state.data?.next_cursor
      ? items.length
      : null;

  const problem = error instanceof ApiError ? error.problem : null;
  const fields = problem ? fieldErrors(problem) : {};

  /**
   * Пересобрать набор заново.
   *
   * Ответ приходит до того, как появится первый вопрос: сайт читается и вопросы
   * пишутся воркером. Поэтому здесь не перечитывание списка, а переход в
   * ожидание — список перечитается, когда подготовка договорит.
   */
  async function rebuild() {
    if (!brandId) return;
    setRebuildAsked(false);
    setBusy('rebuild');
    setError(null);
    setBanner(null);
    try {
      setPrepared(await rebuildQuestions(brandId, attempt.key));
      // Ключ обновляется только на удавшейся попытке: повтор после потерянного
      // ответа обязан уйти С ТЕМ ЖЕ ключом, иначе он станет второй пересборкой.
      attempt.renew();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  function refresh(message: string | null) {
    setBanner(message);
    setMode('list');
    list.reload();
    quotas.reload();
    scope.reload();
  }

  async function patch(question: Question, body: Partial<Question>) {
    setBusy(question.id);
    setError(null);
    try {
      await api.patch<Question>(`/questions/${question.id}`, body);
      list.reload();
      quotas.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  async function saveText() {
    if (!editing) return;
    setBusy(editing.id);
    setError(null);
    try {
      const next = await api.patch<Question>(`/questions/${editing.id}`, {
        text: draft.trim(),
      });
      setEditing(null);
      list.reload();
      setBanner(
        `Заведена версия № ${int(next.version)} вопроса, прошлая выключена. Сравнивать «до» и «после» теперь можно только внутри одной версии.`,
      );
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  async function addManual() {
    if (!brandId) return;
    setBusy('new');
    setError(null);
    try {
      await api.post<Question>(`/brands/${brandId}/questions`, {
        text: newText.trim(),
        funnel_stage: newStage,
      });
      setNewText('');
      refresh('Вопрос добавлен в ядро, заведена новая ревизия.');
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  async function deactivate(question: Question) {
    setRemoving(null);
    setBusy(question.id);
    setError(null);
    try {
      await api.delete<void>(`/questions/${question.id}`);
      list.reload();
      quotas.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  function onBulkDone(result: BulkResult, replaced = false, imported = false) {
    /* Каждое число объясняет разницу между «прислали» и «записали». Молчащий
       счётчик читается как пропажа: клиент прислал пятьсот строк, увидел «310»
       и не знает, что стало с остальными. Ноль не печатается — это шум. */
    const notes: string[] = [];
    if (result.skipped) notes.push(`пропущено ${int(result.skipped)} дублей`);
    if (result.rejected.length) {
      /* Отвергнутое называется ПРИЧИНОЙ, а не числом: «не приняли 12» без слов
         посылает человека искать их глазами среди пятисот строк файла. Причин
         обычно одна на всю пачку, разных — две-три. */
      const reasons = [...new Set(result.rejected.map((item) => item.message))];
      notes.push(
        `не приняли ${int(result.rejected.length)} — ${reasons.slice(0, 2).join('; ')}`,
      );
    }
    if (result.deactivated) notes.push(`выключено прежних ${int(result.deactivated)}`);
    if (result.flagged) {
      notes.push(
        `${int(result.flagged)} с пометкой: это поисковые запросы, а не вопросы покупателя, и ответом на них будет статья, а не список компаний`,
      );
    }
    /* Замена названа заменой по тому, ЧЕГО ПРОСИЛИ, а не по числу выключенных:
       ядро могло быть пустым, и «Добавили» на месте новой версии ядра спрятало бы
       ту самую границу, через которую нельзя сравнивать графики. */
    /* Номер ревизии называется, только если она есть. Пачка, не изменившая
       состав ядра, новой версии не заводит, а у бренда, которому ещё ничего не
       записывали, прежней тоже нет: «Ревизия ядра № undefined» — это не
       мелочь оформления, а число, по которому клиент сверяет графики. */
    const version = result.revision
      ? ` Ревизия ядра № ${int(result.revision.revision)}.`
      : '';
    /* Разрез появится позже самих строк, и молчать об этом нельзя. Клиент,
       открывший ядро сразу после загрузки, видит у всех пятисот строк «Выбор» и
       пустую тему — то есть ровно то, что выглядит поломкой. Стадию и тему
       проставляет модель фоном, и минута ожидания, названная вслух, отличает
       работу от брака. */
    const layout =
      imported && result.created ? ' Стадии и темы проставим в течение минуты.' : '';
    refresh(
      `${replaced ? 'Ядро заменено' : 'Добавили'} ${countOf(result.created, 'вопрос', 'вопроса', 'вопросов')}${
        notes.length ? `, ${notes.join(', ')}` : ''
      }.${version}${layout}`,
    );
  }

  const head = (
    <div className="screen-head">
      <div>
        <h1>Семантическое ядро</h1>
        {/* Счётчик — ОРГАНИЗАЦИИ, а не бренда: `questions_used` приходит из
            `/organizations/current/quotas`. Подписанный именем бренда, он
            противоречил списку под собой: «Альфа Моторс · отслеживается 4 из
            150» над пустым «Ядра ещё нет». */}
        <p className="stamp">
          {scope.brand ? `${scope.brand.name}` : 'Все бренды'}
          {quota
            ? ` · по организации отслеживается ${coverageOf(quota.questions_used ?? 0, quota.tracked_questions, 'вопрос', 'вопроса', 'вопросов')} тарифа`
            : ' · размер ядра определяется тарифом'}
        </p>
      </div>
      {/* Все четыре действия расходуют квоту, и при пустой квоте сервер откажет
          каждому (`402`/`409` в `_ensure_capacity`). Раньше до них не доходило:
          экран целиком закрывала заглушка. Теперь ядро открыто — но вести
          человека в генератор, чтобы отказать ему на сохранении кандидатов,
          было бы той же стеной, только дальше по коридору. Кнопки гаснут и
          объясняются строкой под собой; смотреть и править ядро это не мешает.

          Пока идёт подготовка, кнопок нет вовсе: ядро в конце её заменится
          целиком, и всё добавленное за это время пропало бы без единого слова.
          Что происходит, говорит ожидание под шапкой. */}
      {!readOnly && brandId && mode === 'list' && !preparing ? (
        <>
          <div className="row-actions">
            <button
              className="btn btn-primary btn-sm"
              type="button"
              disabled={noRoom}
              onClick={() => setMode('generate')}
            >
              Придумать вопросы
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={noRoom}
              onClick={() => setMode('packs')}
            >
              Пакеты вопросов
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              /* Замена ядра места не занимает: нынешние вопросы выключатся и
                 освободят свои. Гасить загрузку по пустой квоте значило бы
                 запереть единственный путь, которым клиент с заполненным
                 тарифом может поменять ядро на своё. */
              onClick={() => setMode('import')}
            >
              Загрузить семантику
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              disabled={noRoom}
              onClick={() => setMode('add')}
            >
              Добавить вручную
            </button>
            <button
              className="btn btn-ghost btn-sm"
              type="button"
              /* Без тарифа сервер откажет заведомо (`402`): вести человека в
                 предупреждение о границе версий, чтобы отказать после
                 подтверждения, — это стена на шаг дальше по коридору. Полная
                 квота пересборке НЕ помеха: она переиспользует места нынешнего
                 ядра, поэтому `noRoom` здесь не при чём. */
              disabled={busy === 'rebuild' || noPlan}
              onClick={() => setRebuildAsked(true)}
            >
              Пересобрать вопросы
            </button>
          </div>
          {noRoom ? (
            <p className="stamp">
              {quota && !quota.plan_code
                ? `Добавлять вопросы можно будет после оплаты: ${CORE_SIZE_RULE.toLowerCase()} Ядро до этого открыто на просмотр и правку.`
                : 'Свободных вопросов в тарифе нет, поэтому добавить новые сейчас нельзя. Выключите лишние в ядре или расширьте тариф — вопросы делят все бренды организации.'}
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );

  /* Ожидание и отказ подготовки стоят под шапкой на всех состояниях списка:
     пустое ядро во время пересборки — это не «вопросов нет», а «ещё не
     написаны», и разница для клиента принципиальная. */
  const preparation = preparing ? (
    <Preparing />
  ) : brand?.preparation_state === 'failed' ? (
    <PreparationFailed
      note={brand.preparation_note}
      busy={busy === 'rebuild'}
      onRetry={readOnly ? undefined : () => setRebuildAsked(true)}
    />
  ) : null;

  /* Пересборка спрашивается до нажатия и говорит о последствии, а не о
     действии: набор заменяется целиком, и в истории графиков появляется
     граница, через которую сравнивать нечестно. Диалог один на все состояния
     списка — звать его можно и из шапки, и из полосы отказа. */
  const confirmRebuild = (
    <Confirm
      open={rebuildAsked}
      title="Пересобрать вопросы"
      action="Пересобрать вопросы"
      danger
      onCancel={() => setRebuildAsked(false)}
      onConfirm={() => void rebuild()}
    >
      <p>
        Мы прочитаем сайт заново и напишем набор с нуля. Нынешние вопросы выключатся
        целиком — вместе с правками текстов. Собранные по ним ответы и снимки остаются на
        месте.
      </p>
      <p>
        Это новая версия ядра, то есть граница в истории графиков. Сравнивать «до» и
        «после» через неё нельзя: разница в видимости объясняется сменой вопросов не
        меньше, чем вашей работой.
      </p>
    </Confirm>
  );

  if (scope.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={3} />
      </>
    );
  }

  if (!brandId) {
    return (
      <>
        {head}
        <Empty
          eyebrow="Выберите бренд"
          title="Ядро заводится под конкретный бренд"
          action={
            scope.state.kind === 'ready' && scope.liveBrands.length === 0 ? (
              <Link className="btn btn-primary" to="/brands/new">
                Завести бренд
              </Link>
            ) : undefined
          }
        >
          Ядро принадлежит бренду: вопросы пишутся по его сайту. {pickBrand()}
        </Empty>
      </>
    );
  }

  /* Здесь стоял ранний возврат по `!quota.scan_allowed`, и он прятал ядро
     целиком. `scan_allowed` — это про замеры, а не про ядро: сервер гейтит
     вопросы квотой (`402`/`409` в `_ensure_capacity`), а `scan_allowed`
     проверяет ровно одно место — запуск прогона. У только что заведённой
     организации `scan_allowed` равен `false` всегда, и экран, за который клиент
     платит, отдавал ей заглушку вместо ядра — включая случай, когда вопросы уже
     заведены и их надо просто посмотреть.

     Состояние тарифа теперь говорится полосой ниже, рядом с полосой про
     свободные вопросы, и ничего не закрывает. */

  if (mode === 'generate') {
    return (
      <>
        {head}
        <Generate
          brandId={brandId}
          free={free}
          onDone={onBulkDone}
          onCancel={() => setMode('list')}
        />
      </>
    );
  }

  if (mode === 'import') {
    return (
      <>
        {head}
        <Import
          brandId={brandId}
          free={free}
          own={own}
          /* Сколько строк примет замена. Считается ЗДЕСЬ, где лежат сырые числа
             тарифа, а не в экране загрузки из готового `free`: тот уже прижат к
             нулю, и у организации, вышедшей за тариф (так бывает после
             понижения), «ноль плюс своё ядро» обещало бы места, которых нет.
             Замена освобождает вопросы этого бренда, поэтому занятыми остаются
             чужие: `отслеживается − своё`. */
          replaceRoom={
            quota && own !== null
              ? Math.max(0, quota.tracked_questions - (quota.questions_used ?? 0) + own)
              : null
          }
          /* Третьим доводом — то, что загрузка была загрузкой: раскладку
             фоном получает только чужая семантика, у наших вопросов стадия и
             тема приезжают из шаблона отрасли уже проставленными. */
          onDone={(result, replaced) => onBulkDone(result, replaced, true)}
          onCancel={() => setMode('list')}
        />
      </>
    );
  }

  if (mode === 'packs') {
    return (
      <>
        {head}
        <Packs
          brandId={brandId}
          /* Метка разбора, а не выбор клиента: ею сервер отбирает пакеты, а на
             экране её нет ни в каком виде. */
          verticalCode={brand?.vertical_code ?? ''}
          free={free}
          onDone={onBulkDone}
          onCancel={() => setMode('list')}
        />
      </>
    );
  }

  if (mode === 'add') {
    return (
      <>
        {head}
        <div className="panel">
          <h2 className="panel-h">Добавить вопрос вручную</h2>
          <p className="stamp">
            Пишите так, как это печатает покупатель: «где в Казани купить…», а не «услуги
            автосалона». Имени бренда в вопросе быть не должно — иначе упоминание в ответе
            ничего не доказывает.
          </p>
          {error ? <ErrorNotice error={error} /> : null}
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void addManual();
            }}
          >
            <TextArea
              label="Вопрос покупателя"
              value={newText}
              onChange={setNewText}
              focusOnMount
              error={fields.contains_brand_name ?? fields.text ?? null}
              hint="От восьми знаков. Один вопрос — одна строка"
            />
            <SelectField<FunnelStage>
              label="Стадия воронки"
              value={newStage}
              onChange={setNewStage}
              options={FUNNEL_ORDER.map((stage) => ({
                value: stage,
                label: funnelStage(stage),
              }))}
              hint="Чем ближе к решению, тем дороже стоит попадание в ответ"
            />
            <div className="row-actions">
              <button
                className="btn btn-primary"
                type="submit"
                disabled={busy === 'new' || newText.trim().length < 8}
              >
                Добавить в ядро
              </button>
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => setMode('list')}
              >
                Отмена
              </button>
            </div>
          </form>
        </div>
      </>
    );
  }

  if (list.state.kind === 'loading') {
    return (
      <>
        {head}
        <Skeleton rows={4} />
      </>
    );
  }

  if (list.state.kind === 'failed') {
    const cause = list.state.error;
    const forbidden =
      cause instanceof ApiError && cause.problem.code === 'role_forbidden';
    return (
      <>
        {head}
        {forbidden ? (
          <Empty eyebrow="Не ваша роль" title="Состав вопросов вам не показывается">
            Ядро — это то, что агентство собрало для вас как работу. Отчёты и рекомендации
            открыты, состав вопросов — нет.
          </Empty>
        ) : (
          <ErrorNotice
            error={cause}
            action={
              <button
                className="btn btn-ghost btn-sm"
                type="button"
                onClick={list.reload}
              >
                Повторить
              </button>
            }
          />
        )}
      </>
    );
  }

  if (items.length === 0 && !filtered) {
    return (
      <>
        {head}
        {preparation}
        {error ? <ErrorNotice error={error} /> : null}
        {watch.error ? <ErrorNotice error={watch.error} /> : null}
        {/* Пока набор пишется, «мы пока не знаем, о чём спрашивать» — неправда:
            знаем и как раз пишем. Пустое состояние ждёт своей очереди. */}
        {preparing ? null : (
          <>
            <ScanStopped quota={quota} canPay={canPay} />
            <Empty
              eyebrow="Ядра ещё нет"
              title="Мы пока не знаем, о чём спрашивать"
              action={
                readOnly ? undefined : noRoom ? (
                  // Квота пуста: генератор придумает вопросы, а сохранить их
                  // сервер не даст. Ведём туда, где это чинится, — а не в тупик.
                  canPay ? (
                    <a className="btn btn-primary" href="/plan">
                      Открыть тариф
                    </a>
                  ) : undefined
                ) : (
                  <button
                    className="btn btn-primary"
                    type="button"
                    onClick={() => setMode('generate')}
                  >
                    Придумать вопросы
                  </button>
                )
              }
            >
              Ядро — постоянный набор вопросов, которые ваши покупатели задают нейросетям.
              Мы напишем его по вашему сайту, а вы уберёте лишнее. {CORE_SIZE_RULE}
            </Empty>
            {/* Ритм выбирают, когда бренд только завели, — то есть ровно здесь,
                до первого ядра, а не после месяца оплаченных проходов. */}
            {brandId ? <ScanCadencePicker brandId={brandId} /> : null}
          </>
        )}
        {confirmRebuild}
      </>
    );
  }

  return (
    <>
      {head}

      {preparation}

      {banner ? (
        <div className="notice" role="status">
          <b>Ядро изменилось</b>
          {banner}
          <button type="button" className="linkish" onClick={() => setBanner(null)}>
            Понятно
          </button>
        </div>
      ) : null}

      {error ? <ErrorNotice error={error} /> : null}

      {watch.error ? <ErrorNotice error={watch.error} /> : null}

      <ScanStopped quota={quota} canPay={canPay} />

      {/* Частота стоит здесь, а не в настройках бренда: ядро — это то, ЧТО мы
          спрашиваем, а частота — как часто мы это делаем, и решают их в одну
          минуту. Каждый вопрос ядра стоит денег на каждом проходе. */}
      {brandId ? <ScanCadencePicker brandId={brandId} /> : null}

      {/* Про пустую квоту сказано строкой под погашенными кнопками в шапке:
          там она стоит рядом с тем, что из-за неё не работает. Второй раз то же
          самое здесь — шум. */}

      <div className="filters">
        <SelectField<FunnelStage | ''>
          label="Стадия воронки"
          value={filters.stage}
          onChange={(stage) => setFilters({ ...filters, stage })}
          options={[
            { value: '', label: 'Любая' },
            ...FUNNEL_ORDER.map((stage) => ({
              value: stage,
              label: funnelStage(stage),
            })),
          ]}
        />
        <SelectField
          label="Тема"
          value={filters.cluster}
          onChange={(cluster) => setFilters({ ...filters, cluster })}
          options={[
            { value: '', label: 'Любая' },
            ...clusters.map(([value, label]) => ({ value, label })),
          ]}
        />
        <SelectField
          label="Состояние"
          value={filters.active ? 'active' : 'off'}
          onChange={(value) => setFilters({ ...filters, active: value === 'active' })}
          options={[
            { value: 'active', label: 'В ядре' },
            { value: 'off', label: 'Выключенные' },
          ]}
        />
        {filtered ? (
          <button
            type="button"
            className="linkish"
            onClick={() => setFilters(NO_FILTERS)}
          >
            Сбросить фильтры
          </button>
        ) : null}
      </div>

      {items.length === 0 ? (
        <Empty
          eyebrow="Ничего не нашлось"
          title="Под эти условия вопросов нет"
          action={
            <button
              className="btn btn-ghost"
              type="button"
              onClick={() => setFilters(NO_FILTERS)}
            >
              Сбросить фильтры
            </button>
          }
        >
          В ядре есть вопросы, но ни один не подходит под выбранные стадию, тему или
          состояние.
        </Empty>
      ) : (
        <div className="tbl-wrap">
          <table className="mini questions">
            <caption>
              {filters.active ? 'Вопросы в ядре' : 'Выключенные вопросы'} ·{' '}
              {countOf(items.length, 'вопрос', 'вопроса', 'вопросов')}
              {quota
                ? ` · тариф даёт ${countOf(quota.tracked_questions, 'отслеживаемый вопрос', 'отслеживаемых вопроса', 'отслеживаемых вопросов')} на всю организацию`
                : ''}
            </caption>
            <thead>
              <tr>
                <th scope="col">Вопрос покупателя</th>
                <th scope="col">Тема</th>
                <th scope="col">Стадия</th>
                <th scope="col">Откуда</th>
                <th scope="col">В ядре</th>
                {readOnly ? null : <th scope="col">&nbsp;</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((question) => (
                <tr key={question.id}>
                  <td>
                    {editing?.id === question.id ? (
                      <div className="row-edit">
                        <TextArea
                          label="Новый текст вопроса"
                          value={draft}
                          onChange={setDraft}
                          focusOnMount
                          error={fields.contains_brand_name ?? fields.text ?? null}
                          hint="Прежняя версия вопроса выключится"
                        />
                        <div className="row-actions">
                          <button
                            className="btn btn-primary btn-sm"
                            type="button"
                            disabled={busy === question.id || draft.trim().length < 8}
                            onClick={() => void saveText()}
                          >
                            Сохранить новой версией
                          </button>
                          <button
                            className="btn btn-ghost btn-sm"
                            type="button"
                            onClick={() => setEditing(null)}
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <span className="q-text">{question.text}</span>
                        <span className="stamp">
                          версия {int(question.version)}
                          {question.contains_brand_name
                            ? ' · внутри есть имя бренда — из расчёта видимости выпадает'
                            : ''}
                          {/* Пометка живёт у вопроса, а не на экране загрузки:
                              предупреждение, погасшее вместе с тем экраном, через
                              месяц не отвечает на «почему у нас низкая видимость». */}
                          {question.quality_note
                            ? ` · ${question.quality_note.toLowerCase()}`
                            : ''}
                        </span>
                      </>
                    )}
                  </td>
                  <td>
                    {question.cluster_label ?? <span className="dim">без темы</span>}
                  </td>
                  <td>{funnelStage(question.funnel_stage)}</td>
                  <td>{questionSource(question.source)}</td>
                  <td>
                    {readOnly ? (
                      question.is_active ? (
                        'Да'
                      ) : (
                        <span className="dim">Нет</span>
                      )
                    ) : (
                      <Switch
                        label="Считать в ядре"
                        checked={question.is_active}
                        busy={busy === question.id}
                        onChange={(is_active) =>
                          is_active
                            ? void patch(question, { is_active })
                            : setRemoving(question)
                        }
                      />
                    )}
                  </td>
                  {readOnly ? null : (
                    <td>
                      <button
                        type="button"
                        className="linkish"
                        disabled={busy === question.id}
                        onClick={() => {
                          setError(null);
                          setEditing(question);
                          setDraft(question.text);
                        }}
                      >
                        Изменить
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Confirm
        open={removing !== null}
        title="Убрать вопрос из ядра"
        action="Убрать из ядра"
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && void deactivate(removing)}
      >
        <p>
          Вопрос перестанет уходить движкам и освободит место в тарифе. Собранные по нему
          ответы и снимки остаются на месте.
        </p>
      </Confirm>

      {confirmRebuild}

      {!readOnly && free !== null ? (
        <p className="stamp under-table">
          Свободно {countOf(free, 'вопрос', 'вопроса', 'вопросов')} тарифа. Квота
          покупается на организацию и делится между всеми пространствами и брендами.
        </p>
      ) : null}
    </>
  );
}

/**
 * Замеры остановлены — полосой, а не заглушкой вместо экрана.
 *
 * Ядро принадлежит бренду и живёт независимо от того, идут ли сейчас замеры:
 * его смотрят, правят и готовят к возобновлению. Прятать его за состоянием
 * подписки — это прятать от клиента ровно то, за что он платит.
 */
function ScanStopped({ quota, canPay }: { quota: Quotas | null; canPay: boolean }) {
  if (!quota || quota.scan_allowed) return null;
  /* «Добавлять» обещать можно не всегда: остановленные замеры и пустая квота —
     разные состояния, и совпадают они только у организации без тарифа.
     Приостановленная подписка оставляет квоту на месте, и тогда вопросы
     действительно добавляются; при пустой квоте сервер откажет (`402`/`409`),
     а кнопки добавления на этом экране погашены. Полоса обязана говорить то же
     самое, что делают кнопки под ней. */
  const room = Math.max(0, quota.tracked_questions - (quota.questions_used ?? 0)) > 0;
  // Тарифа нет — это `plan_code`. `scan_allowed` снимается и приостановкой, и
  // перерасходом, поэтому «дождитесь тарифа» по нему говорить нельзя: у клиента
  // с полной квотой лечится это освобождением места, а не оплатой.
  const noPlan = !quota.plan_code;
  return (
    <div className="notice warn" role="status">
      <b>Замеры остановлены</b>
      {quota.scan_blocked_reason ?? scanBlockedFallback()}{' '}
      {/* «Добавлять можно» здесь не обещается даже при свободной квоте:
          вместимость ядра — это минимум из квоты организации и лимита,
          выделенного пространству (`/workspaces/{id}/allocation`), а экран
          видит только первое. У агентства пространство бывает заполнено при
          свободной организации, и сервер ответит `409`. Что доступно, говорят
          сами кнопки; полоса говорит только про замеры. */}
      {room
        ? 'Ядро при этом живое: его можно править и готовить к возобновлению — замер пойдёт по нему, как только замеры возобновятся.'
        : noPlan
          ? 'Ядро при этом открыто на просмотр и правку. Новые вопросы появятся вместе с тарифом: он определяет, сколько их отслеживать.'
          : 'Ядро при этом открыто на просмотр и правку. Свободных вопросов в тарифе не осталось: выключите лишние или расширьте тариф — новые добавятся сразу.'}
      {canPay ? (
        <span className="notice-act">
          <a className="btn btn-ghost btn-sm" href="/plan">
            Открыть тариф
          </a>
        </span>
      ) : null}
    </div>
  );
}
